import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import type { Map as MLMap, Marker } from "maplibre-gl";
import type { Step } from "../lib/nav";
import { formatDistance } from "../lib/nav";
import { ManeuverIcon } from "./ManeuverIcon";

type Props = {
  /** The step we're currently on (incoming road, dim grey). */
  currentStep: Step | null;
  /** The step we'll move onto AFTER the maneuver (outgoing road, accent). */
  nextStep: Step | null;
  /** Metres until the maneuver. Null hides the inset. */
  distanceToManeuver: number | null;
  /** Theme — drives style flavour to match the main map. */
  theme: "day" | "night";
  /** Accent for the outgoing route line, matches the main map. */
  accentHex: string;
};

/** Distance threshold (metres) at which the inset becomes visible. */
const SHOW_DISTANCE = 300;
/** Distance below which we consider the maneuver passed (so it can hide). */
const HIDE_DISTANCE = 20;

const STYLE_URLS: Record<"day" | "night", string> = {
  day: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
  night: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
};

const IN_SRC = "inset-incoming";
const IN_LYR = "inset-incoming";
const OUT_OUTLINE_SRC = "inset-outgoing-outline";
const OUT_OUTLINE_LYR = "inset-outgoing-outline";
const OUT_SRC = "inset-outgoing";
const OUT_LYR = "inset-outgoing";

function emptyLineFC(): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  return { type: "FeatureCollection", features: [] };
}

function lineFC(coords: [number, number][]): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  return {
    type: "FeatureCollection",
    features: [{
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
      properties: {},
    }],
  };
}

export function ManeuverInset({
  currentStep, nextStep, distanceToManeuver, theme, accentHex,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const pinRef = useRef<Marker | null>(null);
  const styleReadyRef = useRef(false);

  const step = nextStep;
  const visible = !!step
    && distanceToManeuver != null
    && distanceToManeuver < SHOW_DISTANCE
    && distanceToManeuver > HIDE_DISTANCE;

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URLS[theme],
      center: [12.5683, 55.6761],
      zoom: 17.5,
      bearing: 0,
      attributionControl: false,
      interactive: false,
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.on("load", () => {
      styleReadyRef.current = true;
      ensureLayers(map, accentHex);
      applyStep(map, currentStep, step, accentHex);
    });
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      pinRef.current?.remove();
      pinRef.current = null;
      map.remove();
      mapRef.current = null;
      styleReadyRef.current = false;
    };
  }, []);

  // Theme swap.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    styleReadyRef.current = false;
    map.setStyle(STYLE_URLS[theme]);
    map.once("styledata", () => {
      styleReadyRef.current = true;
      ensureLayers(map, accentHex);
      applyStep(map, currentStep, step, accentHex);
    });
  }, [theme]);

  // Recenter on the maneuver location + draw lines + pin. The inset is
  // oriented heading-up so the driver's *approach direction points up*,
  // and the upcoming turn naturally falls to its real left/right side
  // visually — matches the icon's directional meaning and the main map.
  // We use OSRM's `bearingBefore` (compass heading entering the
  // maneuver) for that orientation; the inset's tiny tile load isn't
  // worth the CSS-rotation gymnastics we use on the main map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !step) return;
    const [lon, lat] = step.maneuver.location;
    const approachBearing = step.maneuver.bearingBefore ?? 0;
    map.jumpTo({ center: [lon, lat], zoom: 17.5, bearing: approachBearing });

    if (pinRef.current) { pinRef.current.remove(); pinRef.current = null; }
    const el = document.createElement("div");
    el.className = "maneuver-pin";
    pinRef.current = new maplibregl.Marker({
      element: el,
      rotationAlignment: "viewport",
      pitchAlignment: "viewport",
    }).setLngLat([lon, lat]).addTo(map);

    if (styleReadyRef.current) applyStep(map, currentStep, step, accentHex);
  }, [step, currentStep, accentHex]);

  // Force a resize after the inset transitions to visible (it goes from
  // 0×0 hidden to 240×240 on the next frame).
  useEffect(() => {
    if (!visible || !mapRef.current) return;
    const id = window.setTimeout(() => mapRef.current?.resize(), 50);
    return () => clearTimeout(id);
  }, [visible]);

  return (
    <div className={`maneuver-inset ${visible ? "is-visible" : ""}`} aria-hidden={!visible}>
      <div className="maneuver-inset-map" ref={containerRef} />
      <div className="maneuver-inset-pill">
        <span className="maneuver-inset-arrow">
          <ManeuverIcon
            type={step?.maneuver.type}
            modifier={step?.maneuver.modifier}
            size={26}
            strokeWidth={4}
          />
        </span>
        {distanceToManeuver != null && (
          <span className="maneuver-inset-distance tabular">{formatDistance(distanceToManeuver)}</span>
        )}
      </div>
    </div>
  );
}

function ensureLayers(map: MLMap, accent: string) {
  if (!map.getSource(IN_SRC)) map.addSource(IN_SRC, { type: "geojson", data: emptyLineFC() });
  if (!map.getSource(OUT_OUTLINE_SRC)) map.addSource(OUT_OUTLINE_SRC, { type: "geojson", data: emptyLineFC() });
  if (!map.getSource(OUT_SRC)) map.addSource(OUT_SRC, { type: "geojson", data: emptyLineFC() });

  if (!map.getLayer(IN_LYR)) {
    map.addLayer({
      id: IN_LYR,
      type: "line",
      source: IN_SRC,
      paint: { "line-color": "#7b8195", "line-width": 5, "line-opacity": 0.7 },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  }
  if (!map.getLayer(OUT_OUTLINE_LYR)) {
    map.addLayer({
      id: OUT_OUTLINE_LYR,
      type: "line",
      source: OUT_OUTLINE_SRC,
      paint: { "line-color": "#000", "line-width": 11, "line-opacity": 0.35 },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  }
  if (!map.getLayer(OUT_LYR)) {
    map.addLayer({
      id: OUT_LYR,
      type: "line",
      source: OUT_SRC,
      paint: { "line-color": accent, "line-width": 7, "line-opacity": 0.98 },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  } else {
    map.setPaintProperty(OUT_LYR, "line-color", accent);
  }
}

function applyStep(
  map: MLMap,
  currentStep: Step | null,
  nextStep: Step | null,
  accent: string,
) {
  ensureLayers(map, accent);
  const inSrc = map.getSource(IN_SRC) as maplibregl.GeoJSONSource | undefined;
  const outOutlineSrc = map.getSource(OUT_OUTLINE_SRC) as maplibregl.GeoJSONSource | undefined;
  const outSrc = map.getSource(OUT_SRC) as maplibregl.GeoJSONSource | undefined;
  if (inSrc) {
    const c = currentStep?.geometry?.coordinates;
    inSrc.setData(c?.length ? lineFC(c as [number, number][]) : emptyLineFC());
  }
  const oc = nextStep?.geometry?.coordinates;
  if (outOutlineSrc) outOutlineSrc.setData(oc?.length ? lineFC(oc as [number, number][]) : emptyLineFC());
  if (outSrc) outSrc.setData(oc?.length ? lineFC(oc as [number, number][]) : emptyLineFC());
}
